// Shared WAI-ARIA dialog focus management. Extracted from the Story 6.9 Modal so
// the app's FiltersDrawer can reuse the SAME proven trap (PR #139 review: the
// drawer set aria-modal but didn't actually trap Tab or restore focus on close).
//
// While `active`, on mount it (1) captures the currently-focused element, then
// (2) moves focus into `containerRef` (its first tabbable element, or the
// container itself when it has none). On deactivate/unmount it (3) restores focus
// to the captured element — so a dialog opened from a trigger returns focus there
// on close. The returned handler traps Tab / Shift+Tab within the container,
// wrapping at both ends — including when focus rests on the `tabIndex=-1`
// container itself (reached by clicking non-interactive content), where the
// browser default would otherwise escape the dialog.
//
// It handles ONLY focus (not Escape or body-scroll lock) so each dialog composes
// those per its own contract.

import { useCallback, useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';

// Excludes disabled controls and hidden inputs so the first/last focusable is a
// genuinely tabbable element — otherwise `.focus()` no-ops and Tab escapes.
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
): (e: KeyboardEvent<HTMLElement>) => void {
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const focusable = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    (focusable && focusable.length > 0 ? focusable[0] : containerRef.current)?.focus();
    return () => {
      restoreFocusRef.current?.focus?.();
    };
  }, [active, containerRef]);

  return useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Tab') return;
      const focusable = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable || focusable.length === 0) return;
      const list = Array.from(focusable);
      const first = list[0];
      const last = list[list.length - 1];
      if (!first || !last) return;
      // `indexOf` is -1 when focus is on the container itself (tabIndex=-1) or
      // otherwise outside the tabbable set. Without handling that, Shift+Tab
      // matches neither end, the browser default runs, and focus escapes. Wrap to
      // `last` going backward and `first` going forward for that case too.
      const idx = list.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && idx <= 0) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (idx === -1 || idx === list.length - 1)) {
        e.preventDefault();
        first.focus();
      }
    },
    [containerRef],
  );
}
